import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import type { Breadcrumb } from "@/common/types/types";
import { BackgroundVisitUploadPanel } from "@/components/ui/BackgroundVisitUploadPanel";
import { OpenBackgroundVisitUploadButton } from "@/components/ui/BackgroundVisitUploadPanel/OpenBackgroundVisitUploadButton";
import { Button } from "@/components/ui/button";
import { PendingRecordingsButton } from "@/components/ui/offline-recording/PendingRecordingsButton";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

// Redesigned pages (VITE_APP_REDESIGN_ENABLED): the top bar is the page
// header. The title gets a quiet count and the page's primary action sits
// next to the upload button, so the page does not repeat its name below.
export interface PageHeaderBar {
  // Shown as a small pill after the title; `countLabel` is what screen
  // readers hear (e.g. "10 visits").
  count?: number;
  countLabel?: string;
  actions?: React.ReactNode;
}

interface NavbarProps {
  title: string;
  pageHeader?: PageHeaderBar;
  breadcrumbs?: Breadcrumb[];
  icon?: React.ReactNode;
  iconHandler?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  title,
  breadcrumbs,
  icon,
  iconHandler,
  pageHeader,
}) => {
  const [openBackgroundVisitPanel, setOpenBackgroundVisitPanel] =
    useState(false);

  return (
    <header
      className={cn(
        "flex items-center px-6 py-4 border-b",
        pageHeader && "px-4 py-3 sm:px-6",
      )}
      data-testid={pageHeader ? "page-header-bar" : undefined}
    >
      <SidebarTrigger
        className={cn("md:hidden mr-4", pageHeader && "mr-2 size-11 shrink-0")}
      />
      <div className="w-full flex flex-col justify-between gap-2">
        <div className="w-full flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center">
            {!breadcrumbs ? (
              <h1 className="text-lg font-bold text-primary">{title}</h1>
            ) : (
              breadcrumbs?.map((breadcrumb, index) => {
                // The page itself (a visit title can be long) shrinks to one
                // line on phones and two from sm, with an ellipsis; the full
                // name stays in its title and the page heading below.
                const last = index === breadcrumbs.length - 1;
                return (
                  <Link
                    key={index}
                    className={cn(
                      "text-lg font-bold text-primary flex items-center",
                      last ? "min-w-0" : "shrink-0",
                    )}
                    title={last ? breadcrumb.name : undefined}
                    to={breadcrumb.href}
                  >
                    {index !== 0 && (
                      <ChevronRight className="shrink-0" size={18} />
                    )}
                    <span
                      className={cn(
                        last &&
                          "line-clamp-1 min-w-0 wrap-break-word sm:line-clamp-2",
                      )}
                      data-testid={last ? "navbar-current-page" : undefined}
                    >
                      {breadcrumb.name}
                    </span>
                  </Link>
                );
              })
            )}
            {pageHeader?.count !== undefined && (
              <span
                className="ml-2 shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground dark:bg-accent"
                data-testid="page-header-count"
                title={pageHeader.countLabel}
              >
                <span aria-hidden>{pageHeader.count}</span>
                <span className="sr-only">{pageHeader.countLabel}</span>
              </span>
            )}
          </div>
          {pageHeader?.actions && (
            <div
              className="flex shrink-0 items-center gap-2"
              data-testid="page-header-actions"
            >
              {pageHeader.actions}
            </div>
          )}
          {icon && (
            <Button
              className="flex shrink-0 items-center h-6 w-6"
              variant="ghost"
              onClick={iconHandler}
            >
              {/* TO-DO implement a tooltip here to help the user understand what the cloud is for */}
              {icon}
            </Button>
          )}
          <div className="flex shrink-0 items-center gap-1">
            <PendingRecordingsButton />
            <OpenBackgroundVisitUploadButton
              isOpen={openBackgroundVisitPanel}
              onOpenChange={(value) => {
                setOpenBackgroundVisitPanel(value);
              }}
            />
          </div>
        </div>
        <AnimatePresence initial={false}>
          {openBackgroundVisitPanel && (
            <motion.div
              key="background-visit-panel"
              animate={{ height: "auto", opacity: 1 }}
              className="overflow-hidden"
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <BackgroundVisitUploadPanel />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
